import React from 'react';
import qs from 'qs';
import { isEmpty, keyBy, mapValues, isObject, pick } from 'lodash';
import { toast } from 'react-toastify';
import numeral from 'numeral';
import fileDownload from 'js-file-download';
import retry from 'async-retry';
import { isEmail } from 'validator';

import firebase from './firebase';
import env from './env';
import { prefectures } from './shared/config';

const { entries, keys, } = Object;
const prefecturesByPref = mapValues(keyBy(entries(prefectures), '1'), '0');
const db = firebase.firestore();
const placesRef = db.collection('places');

export function fullPathWithParams(params, { pathname, search }, currentParamNames) {
  const currentParams = qs.parse(decodeURI(search.slice(1)));
  const newParams = {
    ...(currentParamNames ? pick(currentParams, currentParamNames) : currentParams),
    ...params,
  };
  const newSearch = qs.stringify(newParams);
  return `${pathname}${newSearch ? `?${newSearch}` : ''}`;
}

export function readFile(file, type = 'readAsText') {
  const reader = new FileReader();
  reader[type](file);
  return new Promise((resolve) => {
    reader.addEventListener('load', (_) => resolve(_.target.result));
  });
}

export async function resizeImage(file, maxWidth = 800) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Canvas toBlob failed'));
            return;
          }
          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        }, file.type);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function checkPlaceUniqueness({ id, name, prefecture }) {
  const { docs } = await placesRef.where('name', '==', name).where('prefecture', '==', prefecture).get();
  return docs.filter((_) => _.id !== id).length === 0;
}

export function answerText(value, type) {
  const radioOrSelect = () => (
    isObject(value) ? (
      Object.entries(value || {})
        .filter((_) => _[1])
        .map((_) => _[0])
        .join(', ')
    ) : value
  );
  return {
    text: _ => value,
    radio: radioOrSelect,
    select: radioOrSelect,
    checkbox: _ => (
      Object.entries(value || {})
        .filter((_) => _[1])
        .map((_) => _[0])
        .join(', ')
    ),
  }[type]?.();
}

export function withConfirm(message, f) {
  return (...args) => {
    if (!window.confirm(message)) return;

    f(...args);
  };
}

export async function getAddressByPostalCode(postalCode) {
  const { results } = await (
    await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${postalCode}`)
  ).json();
  if (isEmpty(results)) return;

  const [
    { address1, address2, address3 },
  ] = results;
  return {
    prefecture: prefecturesByPref[address1],
    city: address2,
    address1: address3,
  };
}

export function fillAddress(prevStatedFields, statedFields) {
  if(!prevStatedFields) return;
  if(prevStatedFields.postalCode.value === statedFields.postalCode.value) return;
  if(!statedFields.postalCode.isValid) return;

  (async () => {
    const data = await getAddressByPostalCode(statedFields.postalCode.value);
    data && statedFields.setValues({
      prefecture: data.prefecture,
      city: data.city,
    });
  })();
}

export function beforeDelete(refs) {
  return async (item) => {
    if((await Promise.all(refs.map(_ => _.limit(1).get()))).some(_ => _.docs.length > 0)) {
      toast.error('使用されているため削除できません');
      return false;
    }
  };
}

export const downloadCanvasImage = (canvas, filename) => {
  const link = document.createElement('a');
  link.href = canvas.toDataURL("image/jpg");
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link)
  }, 100);
}

export const formatDuration = (_seconds) => {
  if (_seconds === 0) return;
  const hours = numeral(Math.trunc(_seconds / 3600)).format('00');
  const minutes = numeral(Math.trunc((_seconds % 3600) / 60)).format('00')
  const seconds = numeral(Math.trunc(_seconds % 60)).format('00');
  return `${hours}:${minutes}:${seconds}`;
};

export const activateRichTextHtml = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const imageNodes = doc.querySelectorAll('img[alt]');
  Array.from(imageNodes).map((imageNode) => {
    if(imageNode.getAttribute('alt')?.startsWith('[video] ')) {
      const videoNode = document.createElement('video');
      videoNode.setAttribute('src', imageNode.getAttribute('alt').replace('[video] ', ''));
      const posterUrl = imageNode.getAttribute('src');
      if(!posterUrl.includes('movie_play')) videoNode.setAttribute('poster', posterUrl);
      videoNode.setAttribute('controls', '');
      imageNode.parentNode.appendChild(videoNode);
      imageNode.parentNode.removeChild(imageNode);
    } else if(imageNode.getAttribute('alt')?.startsWith('[audio] ')) {
      const audioNode = document.createElement('audio');
      audioNode.setAttribute('src', imageNode.getAttribute('alt').replace('[audio] ', ''));
      audioNode.setAttribute('controls', '');
      imageNode.parentNode.appendChild(audioNode);
      imageNode.parentNode.removeChild(imageNode);
    } else {
      const aNode = document.createElement('a');
      aNode.setAttribute('href', imageNode.getAttribute('alt'));
      aNode.setAttribute('target', '_blank');
      imageNode.parentNode.appendChild(aNode);
      aNode.appendChild(imageNode);
      imageNode.removeAttribute('alt');
    }
  });
  const anchorNodes = doc.querySelectorAll('a[href]');
  /*
  !disablesTargetBlank && Array.from(anchorNodes).map((anchorNode) => {
    anchorNode.setAttribute('target', '_blank');
  });
  */
  return doc.body.innerHTML;
};

export const answerDisplayValue = (answer) => {
  return (
    isObject(answer) ? (
      [answer].flat().some(_ => _?.url) ? (
        [answer].flat().map((answer, i) => (
          <div key={i}>
            <a href={answer?.url} target="_blank">
              {answer?.name}
            </a>
          </div>
        ))
      ) : keys(answer).join(', ')
    ) : answer || ''
  );
};

export function autoLink(text, { linkAttr = {} } = {}) {
  return text.replace(/((http|https|ftp):\/\/[\w?=&.\/-;#~%-]+(?![\w\s?&.\/;#~%"=-]*>))/g, `<a href="$1" ${entries(linkAttr).map(([k, v]) => `${k}="${v}"`).join(' ')}>$1</a> `);
};

export function nl2br(text) {
  return text.replace(/(\n|\r\n)/g, '<br />');
};

export const sanitizePdfHtml = (html) => html
  .replace(/<script.*?>.*?<\/script>/gi, '')
  .replace(/\/static/gi, `${env('HOSTING_HOST')}/static`);

export const fetchPdf = (_html, options = null, _launchOptions = null) => {
  const url = `${env('CLOUD_FUNCTIONS_ENDPOINT')}/html2Pdf`;
  const html = sanitizePdfHtml(_html || document.documentElement.outerHTML);
  const pdfOptions = {
    margin: { top: 60, bottom: 60, },
    format: 'a4',
    ...options,
  };
  const launchOptions = {
    deviceScaleFactor: 10,
    ..._launchOptions,
  };
  const body = JSON.stringify({ html, pdfOptions, launchOptions, });
  const headers = { 'Content-Type': 'application/json' };
  return retry(() => {
    return fetch(url, { method: 'POST', body, headers })
      .then(_ => _.blob());
  }, { retries: 3 });
};

export async function downloadDocument (selector, title) {
  const pageNode = document.querySelector('html').cloneNode(true);
  pageNode.querySelector('body').innerHTML = document.querySelector(selector).innerHTML;
  const pdf = await fetchPdf(pageNode.outerHTML, { printBackground: true });
  fileDownload(pdf, `${title}.pdf`);
};

export function buildInquiryPdfHtml (selector, { hidePersonalInfo = false } = {}) {
  const pageNode = document.querySelector('html').cloneNode(true);
  pageNode.querySelector('body').innerHTML = document.querySelector(selector).innerHTML;
  if (hidePersonalInfo) {
    pageNode.querySelectorAll('.personal-info').forEach(_ => _.remove());
  }
  const imageItems = Array.from(pageNode.querySelectorAll('a[href]'))
    .filter(_ => /\.(jpg|jpeg|png|gif|webp|bmp|heic|heif)/i.test(_.href))
    .map(_ => ({ url: _.href, label: _.closest('tr')?.querySelector('th')?.textContent || '' }));
  if (imageItems.length > 0) {
    const imagesHtml = imageItems.map(_ => `<div style="margin-top: 16px;"><div style="font-weight: bold;">${_.label}</div><img src="${_.url}" style="max-width: 100%;" /></div>`).join('');
    pageNode.querySelector('body').innerHTML += imagesHtml;
  }
  return pageNode.outerHTML;
};

export const inquiryPdfOptions = { printBackground: true, margin: { top: 40, bottom: 40, left: 40, right: 40 } };

export async function generateInquiryPdfBlob (selector, { hidePersonalInfo = false } = {}) {
  const html = buildInquiryPdfHtml(selector, { hidePersonalInfo });
  return fetchPdf(html, inquiryPdfOptions);
};

export async function downloadInquiryPdf (selector, title, { hidePersonalInfo = false } = {}) {
  const pdf = await generateInquiryPdfBlob(selector, { hidePersonalInfo });
  fileDownload(pdf, `${title}.pdf`);
};

export const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result.split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

export const pdfEmailFields = {
  to: {
    label: '送信先メールアドレス',
    type: 'string',
    inputType: 'email',
    validations: {
      required: _ => !isEmpty(_),
      format: _ => !_ || isEmail(_),
    },
  },
  subject: {
    label: '件名',
    type: 'string',
    validations: {
      required: _ => !isEmpty(_),
    },
  },
  body: {
    label: 'メール本文',
    type: 'text',
    validations: {
      required: _ => !isEmpty(_),
    },
  },
  includePersonalInfo: { label: '個人情報を含める', type: 'boolean', initialValue: false },
};

export const translatablePdfEmailFields = {
  ...pdfEmailFields,
  includesEnglish: { label: '英語版PDFも添付', type: 'boolean', initialValue: false },
  includesChinese: { label: '中国語（簡体字）版PDFも添付', type: 'boolean', initialValue: false },
};

export const pdfTranslationLanguages = (values) => [values.includesEnglish && 'en', values.includesChinese && 'zh'].filter(_ => _);

export const toPdfAttachedFiles = (data) => data.files.map(_ => ({ ..._, type: 'application/pdf' }));

export const PdfEmailConfirmBody = ({ values }) => (
  <div>
    <div>以下の内容でPDFをメール送信します。よろしいですか？</div>
    <table className="table table-bordered mt-3">
      <tbody>
        <tr>
          <th className="bg-light" style={{ width: '30%' }}>送信先</th>
          <td>{values.to}</td>
        </tr>
        <tr>
          <th className="bg-light">件名</th>
          <td>{values.subject}</td>
        </tr>
        <tr>
          <th className="bg-light">本文</th>
          <td style={{ whiteSpace: 'pre-line' }}>{values.body}</td>
        </tr>
        <tr>
          <th className="bg-light">個人情報</th>
          <td>{values.includePersonalInfo ? '含める' : '含めない'}</td>
        </tr>
        {(values.includesEnglish || values.includesChinese) && (
          <tr>
            <th className="bg-light">翻訳版</th>
            <td>{[values.includesEnglish && '英語', values.includesChinese && '中国語（簡体字）'].filter(_ => _).join('、')}</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

export async function updateQuestionStatistics(answers) {
  const questionIds = keys(answers);
  if (questionIds.length === 0) return;

  const batch = db.batch();
  const now = new Date();

  questionIds.forEach((questionId) => {
    const questionRef = db.collection('questions').doc(questionId);
    batch.update(questionRef, {
      answerCount: firebase.firestore.FieldValue.increment(1),
      lastAnsweredAt: now,
    });
  });

  await batch.commit();
};

// 送信された本文がAI下書きのままか、担当者が書き直したかを記録するためのフラグ。
// そのまま送られた返信を参考事例プールに入れると、AIが自分の出力を学習し続けて文体が痩せていく。
export const aiDraftReplyUsage = (aiDraftReply, sentBody) => {
  const draft = aiDraftReply?.body;
  if (!draft) return {};

  return { basedOnAiDraft: true, ...(draft.trim() === (sentBody || '').trim() && { sentAiDraftAsIs: true }) };
};
